import React from 'react';
import { useLang } from '../../i18n/LanguageContext';
import { motion } from 'framer-motion';
import {
  FileText, DollarSign, CheckCircle2, Clock, Search, RefreshCw, Plus,
  Edit, Send, Check, Ban, Eye, MessageCircle, Bot, User, LayoutTemplate, ExternalLink,
} from 'lucide-react';

// ─── Configuration des statuts ────────────────────────────────────────────────
export const STATUS_CONFIG = {
  draft:     { label: 'Brouillon', cls: 'bg-gray-500/20 text-gray-300 border border-gray-500/30' },
  sent:      { label: 'Envoyée',   cls: 'bg-blue-500/20 text-blue-500 border border-blue-500/30' },
  paid:      { label: 'Payée',     cls: 'bg-green-500/20 text-green-500 border border-green-500/30' },
  overdue:   { label: 'En retard', cls: 'bg-orange-500/20 text-orange-500 border border-orange-500/30' },
  cancelled: { label: 'Annulée',   cls: 'bg-red-500/20 text-red-500 border border-red-500/30' },
};

export const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'draft', label: 'Brouillons' },
  { id: 'sent', label: 'Envoyées' },
  { id: 'paid', label: 'Payées' },
  { id: 'overdue', label: 'En retard' },
  { id: 'cancelled', label: 'Annulées' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const formatMoney = (amount, currency = 'EUR') => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

export const formatDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const isAgentCreated = (inv) =>
  inv?.createdBy === 'agent' ||
  inv?.source === 'agent' ||
  inv?.metadata?.source === 'whatsapp_ai';

const getClientName = (inv) =>
  inv?.clientName || inv?.client?.name || inv?.client || 'Client';

const getConversationId = (inv) =>
  inv?.conversationId || inv?.metadata?.conversationId || inv?.conversation?._id || null;

// ─── Boutons d'action par ligne (selon statut) ────────────────────────────────
function RowActions({ invoice, handlers, busy }) {
  const { t } = useLang();
  const status = invoice.status;
  const isDraft = status === 'draft';
  const isOpen = status === 'sent' || status === 'overdue'; // facture active, non payée
  const hasPdf = status !== 'draft';

  const btn =
    'p-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed';

  const convId = getConversationId(invoice);

  return (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      {convId && (
        <button
          onClick={() => handlers.onViewConversation(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-blue-500/20 text-blue-400`}
          title={t.factures.tooltipViewConv}
        >
          <ExternalLink size={18} />
        </button>
      )}
      {isDraft && (
        <button
          onClick={() => handlers.onEdit(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-accent-green/20 text-accent-green`}
          title={t.factures.tooltipEdit}
        >
          <Edit size={18} />
        </button>
      )}
      {isDraft && (
        <button
          onClick={() => handlers.onSend(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-blue-500/20 text-blue-500`}
          title={t.factures.tooltipSend}
        >
          <Send size={18} />
        </button>
      )}
      {hasPdf && (
        <button
          onClick={() => handlers.onViewPdf(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-purple-500/20 text-purple-400`}
          title={t.factures.tooltipViewPdf}
        >
          <Eye size={18} />
        </button>
      )}
      {isOpen && (
        <button
          onClick={() => handlers.onSendWhatsApp(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-emerald-500/20 text-emerald-400`}
          title={t.factures.tooltipWhatsApp}
        >
          <MessageCircle size={18} />
        </button>
      )}
      {isOpen && (
        <button
          onClick={() => handlers.onMarkPaid(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-green-500/20 text-green-500`}
          title={t.factures.tooltipMarkPaid}
        >
          <Check size={18} />
        </button>
      )}
      {(isDraft || isOpen) && (
        <button
          onClick={() => handlers.onCancel(invoice)}
          disabled={busy}
          className={`${btn} hover:bg-red-500/20 text-red-500`}
          title={t.factures.tooltipCancel}
        >
          <Ban size={18} />
        </button>
      )}
    </div>
  );
}

function OriginBadge({ invoice }) {
  const { t } = useLang();
  return isAgentCreated(invoice) ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-semibold whitespace-nowrap">
      <Bot size={10} /> {t.factures.aiAgent}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded text-[10px] font-semibold whitespace-nowrap">
      <User size={10} /> {t.factures.manual}
    </span>
  );
}

// ─── Composant présentation ───────────────────────────────────────────────────

/**
 * Composant de présentation des factures (sans appel API).
 * Reçoit les données + les handlers de FacturesConnected.
 */
export default function Factures({
  invoices,
  metrics,
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  onRefresh,
  onNew,
  onCustomizeTemplate,
  onRowClick,
  handlers,
  busyId,
}) {
  const { t, lang } = useLang();

  const statusLabels = {
    draft: t.factures.draft,
    sent: t.factures.sent,
    paid: t.factures.paid,
    overdue: t.factures.overdue,
    cancelled: t.factures.cancelled,
  };

  const translatedFilters = [
    { id: 'all', label: t.factures.filterAll },
    { id: 'draft', label: t.factures.filterDraft },
    { id: 'sent', label: t.factures.filterSent },
    { id: 'paid', label: t.factures.filterPaid },
    { id: 'overdue', label: t.factures.filterOverdue },
    { id: 'cancelled', label: t.factures.filterCancelled },
  ];

  const formatMoneyLocale = (amount, currency = 'EUR') => {
    const n = Number(amount) || 0;
    const locale = lang === 'en' ? 'en-US' : 'fr-FR';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n);
    } catch {
      return `${n.toFixed(2)} ${currency}`;
    }
  };

  const formatDateLocale = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const locale = lang === 'en' ? 'en-US' : 'fr-FR';
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const hasFilters = searchQuery || filterStatus !== 'all';

  return (
    <div>
      {/* ── En-tête de page ────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t.factures.title}
          </p>
          <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>
            {t.factures.subtitleAI}
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto flex-shrink-0">
          <button className="btn-ghost" onClick={onCustomizeTemplate} title={t.factures.template}>
            <LayoutTemplate size={14} />
            {t.factures.template}
          </button>
          <button className="btn-primary" onClick={onNew}>
            <Plus size={14} strokeWidth={2} />
            {t.factures.newInvoice}
          </button>
        </div>
      </div>

      {/* ── Métriques ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: FileText, label: t.factures.totalLabel, value: metrics.total, delay: 0 },
          { icon: DollarSign, label: t.factures.collected, value: formatMoneyLocale(metrics.paidAmount, metrics.currency), delay: 0.06 },
          { icon: Clock, label: t.factures.draftsCount, value: metrics.draftCount, delay: 0.12 },
          { icon: CheckCircle2, label: t.factures.toCollect, value: formatMoneyLocale(metrics.outstandingAmount, metrics.currency), delay: 0.18 },
        ].map((metric, i) => {
          const MetricIcon = metric.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: metric.delay, duration: 0.3, ease: 'easeOut' }}
              className="card-metric p-5"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
              >
                <MetricIcon size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
              </div>
              <p className="text-label mb-1">{metric.label}</p>
              <p className="font-metric text-ink" style={{ fontSize: '24px', fontWeight: 600, lineHeight: 1 }}>
                {metric.value}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* ── Recherche + filtres ────────────────────────────────────────────── */}
      <div className="card p-4 mb-5 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search
              size={14} strokeWidth={1.5}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--c-text-muted)' }}
            />
            <input
              type="text"
              placeholder={t.factures.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-sm pl-9 pr-3 py-2 rounded-lg outline-none text-ink"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              onFocus={e => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
              onBlur={e => (e.target.style.borderColor = 'var(--c-border)')}
            />
          </div>
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg transition-all"
            style={{ background: 'var(--c-hover)' }}
            title="Rafraîchir"
          >
            <RefreshCw size={15} strokeWidth={1.5} style={{ color: 'var(--c-text-muted)' }} />
          </button>
        </div>

        {/* Onglets de statut */}
        <div className="flex gap-2 flex-wrap">
          {translatedFilters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setFilterStatus(filter.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                background: filterStatus === filter.id ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-hover)',
                color: filterStatus === filter.id ? '#fff' : 'var(--c-text-gray)',
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-label">
          {t.factures.invoiceCount(invoices.length)}
        </span>
      </div>

      {/* ── Table / vide ───────────────────────────────────────────────────── */}
      {invoices.length === 0 ? (
        <div className="card p-8 sm:p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--c-text-muted)' }} />
          <h3 className="text-ink font-semibold mb-2" style={{ fontSize: '16px' }}>
            {hasFilters ? t.factures.noResults : t.factures.noInvoices}
          </h3>
          <p className="text-label mb-6">
            {hasFilters ? t.factures.adjustFilters : t.factures.createFirst}
          </p>
          {!hasFilters && (
            <button className="btn-primary" onClick={onNew}>
              <Plus size={14} />
              {t.factures.newInvoice}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Tableau — desktop / tablette */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="hidden md:block card overflow-x-auto"
          >
            <table className="w-full min-w-[900px]">
              <thead style={{ background: 'var(--c-primary-sidebar)' }}>
                <tr style={{ borderBottom: '0.5px solid var(--c-border)' }}>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableInvoiceNum}</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableClient}</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableOrigin}</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableAmount}</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableStatus}</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableDueDate}</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-label uppercase tracking-wider">{t.factures.tableActions}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, index) => {
                  const statusCfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                  return (
                    <tr
                      key={inv._id}
                      onClick={() => onRowClick(inv)}
                      className="transition-colors cursor-pointer hover:bg-[var(--c-hover)]"
                      style={{ borderBottom: '0.5px solid var(--c-border)' }}
                    >
                      <td className="px-5 py-4">
                        <span className="text-accent-green font-mono font-semibold text-sm">{inv.invoiceNumber || '—'}</span>
                      </td>
                      <td className="px-5 py-4 text-ink font-medium">{getClientName(inv)}</td>
                      <td className="px-5 py-4"><OriginBadge invoice={inv} /></td>
                      <td className="px-5 py-4 text-ink font-semibold">
                        {formatMoneyLocale(inv.total, inv.currency)}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${statusCfg.cls}`}>
                          {statusLabels[inv.status] || statusCfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-label text-sm">{formatDateLocale(inv.dueDate)}</td>
                      <td className="px-5 py-4">
                        <RowActions invoice={inv} handlers={handlers} busy={busyId === inv._id} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          {/* Cartes — mobile */}
          <div className="md:hidden space-y-3">
            {invoices.map((inv) => {
              const statusCfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
              return (
                <motion.div
                  key={inv._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => onRowClick(inv)}
                  className="card p-4 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-accent-green font-medium">{inv.invoiceNumber || '—'}</span>
                        <OriginBadge invoice={inv} />
                      </div>
                      <p className="text-ink font-medium truncate mt-1">{getClientName(inv)}</p>
                      <p className="text-label text-xs mt-1">{t.factures.dueDate} : {formatDateLocale(inv.dueDate)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs flex-shrink-0 ${statusCfg.cls}`}>
                      {statusLabels[inv.status] || statusCfg.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-ink font-semibold whitespace-nowrap">
                      {formatMoneyLocale(inv.total, inv.currency)}
                    </p>
                    <RowActions invoice={inv} handlers={handlers} busy={busyId === inv._id} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
